// client/src/components/common/EmailPreferences.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import ConfirmModal from './ConfirmModal';
// ✅ FIXED: Import API_URL
import { API_URL } from '../../services/api';

const EmailPreferences = ({ userRole }) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using dynamic API_URL
      const response = await axios.get(`${API_URL}/email-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPreferences(response.data.preferences || {});
    } catch (err) {
      console.error('Error fetching email preferences:', err);
      setError(t('emailPreferences.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const newValue = !preferences[key];
    
    // Optimistic update
    setPreferences(prev => ({ ...prev, [key]: newValue }));
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using dynamic API_URL
      await axios.put(
        `${API_URL}/email-preferences`,
        { [key]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(t('emailPreferences.preferencesUpdated'));
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error updating preference:', err);
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !newValue }));
      setError(t('emailPreferences.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    setConfirmModal({
      title: t('emailPreferences.resetTitle', 'Reset Preferences'),
      message: t('emailPreferences.resetConfirm'),
      icon: 'warning',
      confirmLabel: t('emailPreferences.resetButton', 'Reset'),
      confirmColor: 'yellow',
      onConfirm: async () => {
        setConfirmModal(null);
        setSaving(true);
        setError('');
        setSuccess('');

        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API_URL}/email-preferences/reset`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPreferences(response.data.preferences || {});
          setSuccess(t('emailPreferences.resetSuccess'));
          setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
          console.error('Error resetting preferences:', err);
          setError(t('emailPreferences.updateError'));
        } finally {
          setSaving(false);
        }
      }
    });
  };

  // Email preference options based on role
  const getPreferenceOptions = () => {
    if (userRole === 'wholesaler') {
      return [
        {
          key: 'orderReceived',
          label: t('emailPreferences.orderReceived.label'),
          description: t('emailPreferences.orderReceived.description')
        }
      ];
    }

    // Business user options
    return [
      {
        key: 'orderPlaced',
        label: t('emailPreferences.orderPlaced.label'),
        description: t('emailPreferences.orderPlaced.description')
      },
      {
        key: 'orderConfirmed',
        label: t('emailPreferences.orderConfirmed.label'),
        description: t('emailPreferences.orderConfirmed.description')
      },
      {
        key: 'orderModified',
        label: t('emailPreferences.orderModified.label'),
        description: t('emailPreferences.orderModified.description')
      },
      {
        key: 'invoiceUploaded',
        label: t('emailPreferences.invoiceUploaded.label'),
        description: t('emailPreferences.invoiceUploaded.description')
      },
      {
        key: 'orderCancelled',
        label: t('emailPreferences.orderCancelled.label'),
        description: t('emailPreferences.orderCancelled.description')
      },
      {
        key: 'orderDelivered',
        label: t('emailPreferences.orderDelivered.label'),
        description: t('emailPreferences.orderDelivered.description')
      },
      {
        key: 'discountOffered',
        label: t('emailPreferences.discountOffered.label'),
        description: t('emailPreferences.discountOffered.description')
      }
    ];
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md mb-8">
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md mb-8">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">📧</span>
          <div className="text-left">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('emailPreferences.title')}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              {t('emailPreferences.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          {/* Reset Button */}
          <div className="flex justify-end pt-4 pb-2">
            <button
              onClick={handleResetDefaults}
              disabled={saving}
              className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
            >
              {t('emailPreferences.resetDefaults')}
            </button>
          </div>

          {/* Success/Error Messages */}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              ✓ {success}
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              ✗ {error}
            </div>
          )}

          {/* Preference Toggles */}
          <div className="space-y-4">
            {getPreferenceOptions().map((option) => (
              <div
                key={option.key}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1">
                  <label
                    htmlFor={option.key}
                    className="font-medium text-gray-900 cursor-pointer"
                  >
                    {option.label}
                  </label>
                  <p className="text-sm text-gray-600 mt-0.5">{option.description}</p>
                </div>

                {/* Toggle Switch */}
                <button
                  id={option.key}
                  type="button"
                  onClick={() => handleToggle(option.key)}
                  disabled={saving}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${preferences[option.key] ? 'bg-blue-600' : 'bg-gray-300'}
                  `}
                  role="switch"
                  aria-checked={preferences[option.key]}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                      transition duration-200 ease-in-out
                      ${preferences[option.key] ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Info Note */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              ℹ️ {t('emailPreferences.note')}
            </p>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default EmailPreferences;